'use client';

import { useEffect, useRef } from 'react';

interface UseIntersectionObserverOptions {
  /** Callback fired when element intersects with viewport */
  onIntersect: () => void;
  /** Whether the observer is enabled (default: true) */
  enabled?: boolean;
  /** Intersection threshold 0-1 (default: 0) */
  threshold?: number;
  /** Root margin for triggering before element is visible (default: '0px') */
  rootMargin?: string;
  /**
   * Root element for intersection detection.
   * - `null` (default): uses the viewport
   * - `'nearest'`: auto-detects the nearest scrollable ancestor of the observed element
   * - `RefObject`: uses the element referenced by the ref
   */
  root?: React.RefObject<Element | null> | 'nearest' | null;
}

function getScrollableAncestor(element: Element): Element | null {
  let current = element.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * A reusable hook for detecting when an element enters the viewport
 * using the native IntersectionObserver API.
 *
 * @example
 * ```tsx
 * const sentinelRef = useIntersectionObserver({
 *   onIntersect: () => fetchNextPage(),
 *   enabled: hasNextPage && !isFetchingNextPage,
 *   rootMargin: '100px', // Trigger 100px before visible
 * });
 *
 * return <div ref={sentinelRef} />;
 * ```
 *
 * @example
 * ```tsx
 * // Auto-detect scroll container for nested scroll contexts
 * const sentinelRef = useIntersectionObserver({
 *   onIntersect: () => fetchNextPage(),
 *   enabled: hasNextPage,
 *   root: 'nearest',
 * });
 * ```
 */
export function useIntersectionObserver<T extends HTMLElement = HTMLDivElement>({
  onIntersect,
  enabled = true,
  threshold = 0,
  rootMargin = '0px',
  root = null,
}: UseIntersectionObserverOptions) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!enabled) return;

    const element = ref.current;
    if (!element) return;

    let resolvedRoot: Element | null = null;
    if (root === 'nearest') {
      resolvedRoot = getScrollableAncestor(element);
    } else if (root) {
      resolvedRoot = root.current;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          onIntersect();
        }
      },
      { threshold, rootMargin, root: resolvedRoot }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [enabled, threshold, rootMargin, onIntersect, root]);

  return ref;
}
